import React from 'react';
import { Calendar, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

/**
 * RevisionMeta — compact "Created / Published / Review by" strip.
 *
 * Renders three date chips (created_at, published_at, review_by) and a
 * review-status pill:
 *   • "Review due" (rose) when review_by is in the past
 *   • "Review soon" (amber) when review_by is within the next 30 days
 *   • "Up to date" (emerald) otherwise
 *
 * Used on CourseCard, LessonCard, and the CourseWorkspace header so
 * admins can see at a glance which lessons need refreshing.
 */
const fmt = (v) => {
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

const reviewPill = (reviewBy) => {
  if (!reviewBy) return null;
  const d = new Date(reviewBy);
  if (Number.isNaN(d.getTime())) return null;
  const now = Date.now();
  const diff = d.getTime() - now;
  const day = 24 * 60 * 60 * 1000;
  if (diff < 0) {
    return { cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200', Icon: AlertTriangle, label: 'Review due' };
  }
  if (diff < 30 * day) {
    return { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200', Icon: Clock, label: 'Review soon' };
  }
  return { cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200', Icon: CheckCircle2, label: 'Up to date' };
};

const RevisionMeta = ({
  createdAt,
  publishedAt,
  reviewBy,
  size = 'sm',
  showPill = true,
  className = '',
  testIdPrefix = 'revision',
}) => {
  const created = fmt(createdAt);
  const published = fmt(publishedAt);
  const review = fmt(reviewBy);
  const pill = showPill ? reviewPill(reviewBy) : null;

  if (!created && !published && !review && !pill) return null;

  const chipCls =
    size === 'xs'
      ? 'text-[10px] px-1.5 py-0.5'
      : 'text-xs px-2 py-0.5';

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
      data-testid={`${testIdPrefix}-meta`}
    >
      {created && (
        <span
          className={`inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 ${chipCls}`}
          title="Date this content was first created"
          data-testid={`${testIdPrefix}-created`}
        >
          <Calendar className="w-3 h-3" />
          Created {created}
        </span>
      )}
      {published && (
        <span
          className={`inline-flex items-center gap-1 rounded-md border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 ${chipCls}`}
          title="Date this content went live to learners"
          data-testid={`${testIdPrefix}-published`}
        >
          <CheckCircle2 className="w-3 h-3" />
          Published {published}
        </span>
      )}
      {review && (
        <span
          className={`inline-flex items-center gap-1 rounded-md border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 ${chipCls}`}
          title="Date this content should be reviewed / refreshed"
          data-testid={`${testIdPrefix}-review-by`}
        >
          <Clock className="w-3 h-3" />
          Review by {review}
        </span>
      )}
      {pill && (
        <span
          className={`inline-flex items-center gap-1 rounded-full font-semibold ${chipCls} ${pill.cls}`}
          data-testid={`${testIdPrefix}-status-pill`}
        >
          <pill.Icon className="w-3 h-3" />
          {pill.label}
        </span>
      )}
    </div>
  );
};

export default RevisionMeta;
